<template>
  <PasswordProtected
    v-if="needsPasswordValidation"
    :incorrect-password="incorrectPassword"
    @submit-password="submitPassword"
  />
  <div
    v-else
    id="page"
    :class="{ 'page-renderer-package-enabled': isPageRendererPackageEnabled }"
  >
    <a-builder
      v-if="isPageRendererPackageEnabled && isContentReady"
      is-render-mode
      :model-value="getBuilderConfig"
    />
    <template v-else>
      <RowRenderer
        v-for="row in page?.content ?? []"
        :key="row.id"
        :row="row"
      />
    </template>
  </div>
</template>

<script setup>
import { computed, ref, onMounted } from 'vue';
import { useMainStore } from '../stores/mainStore.js';
import { useTranslationStore } from '../stores/translationStore.js';
import { useDebounceFn } from '@vueuse/core';
import PasswordProtected from './PasswordProtected.vue';
import RowRenderer from './cms/RowRenderer.vue';

const props = defineProps({
  isPreview: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['page-loaded']);

const mainStore = useMainStore();
const translationStore = useTranslationStore();

const incorrectPassword = ref(false);
const needsPasswordValidation = ref(false);
const formattedInPageSharedContent = ref({});
const isContentReady = ref(false);

const globals = computed(() => mainStore.globals);
const featureFlags = computed(() => mainStore.featureFlags);
const page = computed(() => mainStore.page);
const inPageSharedContent = computed(() => mainStore.inPageSharedContent);
const getFullSiteMap = computed(() => mainStore.getFullSiteMap);
const getSecondaryOrgId = computed(() => mainStore.secondaryOrgId);
const pathPrefix = computed(() => mainStore.pathPrefix);
const getLocale = computed(() => translationStore.getLocale);

const isPageRendererPackageEnabled = computed(
  () => !!featureFlags.value?.cms_page_renderer_package_enabled && page.value?.flags?.new_renderer_enabled,
);

const runtimeConfig = useRuntimeConfig();

const getBuilderConfig = computed(() => ({
  ...page.value?.content,
  customProperties: {
    global: {
      backgroundColorAllowed: true,
      backgroundImageAllowed: true,
    },
    CONTENT_NODE_NAVIGATION: {
      siteMenu: getFullSiteMap.value || null,
    },
  },
  theme: {
    mainColor: globals.value?.info?.page_builder_theme_color?.hex,
    headingColor: globals.value?.info?.cms_heading_color?.hex,
    linkColor: globals.value?.info?.links_color?.hex,
    primaryColor: globals.value?.info?.primary_color?.hex,
    secondaryColor: globals.value?.info?.secondary_color?.hex,
  },
  api: {
    forms: `${runtimeConfig.public.FORMS_SERVICE_API_URL}api/v1/`,
    secondaryOrgId: getSecondaryOrgId.value,
    pathPrefix: pathPrefix.value,
  },
  sharedContent: {
    content: formattedInPageSharedContent.value,
  },
  locale: getLocale.value,
}));

const emitPageLoaded = useDebounceFn(() => {
  emit('page-loaded');
}, 300);

function formatPageSharedContent() {
  const activeSharedContent = inPageSharedContent.value.filter((sharedContent) => sharedContent.status === 'active');
  return activeSharedContent.reduce((acc, { id, name, content }) => {
    acc[id] = { name, content };
    return acc;
  }, {});
}

async function submitPassword(password) {
  const pageContentUrl = `api/v4/o/${getSecondaryOrgId.value}/cms/pages/${page.value.slug}/unlock?locale=${page.value.langCode}`;
  const { $axios } = useNuxtApp();

  try {
    const { data } = await $axios.post(pageContentUrl, { password });
    await mainStore.setPageData(data);
    grantAccess();
  } catch {
    incorrectPassword.value = true;
  }
}

function grantAccess() {
  incorrectPassword.value = false;
  needsPasswordValidation.value = false;
}

needsPasswordValidation.value = !!page.value?.password_protected && !props.isPreview;

onMounted(async () => {
  if (isPageRendererPackageEnabled.value) {
    formattedInPageSharedContent.value = formatPageSharedContent();
  }
  if (mainStore.pageContentNeedsRefresh) {
    mainStore.setPageContentNeedsRefresh(false);
    globalThis.location.reload();
  }
  isContentReady.value = true;
  emitPageLoaded();
});

defineExpose({
  needsPasswordValidation,
  incorrectPassword,
  isPageRendererPackageEnabled,
  getBuilderConfig,
  submitPassword,
  formatPageSharedContent,
  grantAccess,
});
</script>

<style lang="scss" scoped>
@use 'global/scss/mixins/breakpoints';

#page {
  min-height: 500px;
  display: block;
  margin-bottom: 15px;
  &.page-renderer-package-enabled {
    margin-bottom: 0;
  }
}
/** Extra Small to Large Phones **/
@include breakpoints.screen('xs') {
  .row {
    display: block;
  }
  .column {
    display: inline-block;
    width: 100%;
  }
  .ts-left-sidebar {
    &.column:nth-child(1) {
      width: 100%;
    }
    &.column:nth-child(2) {
      width: 100%;
      margin-left: 0;
    }
  }
  .ts-right-sidebar {
    &.column:nth-child(1) {
      width: 100%;
    }
    &.column:nth-child(2) {
      width: 100%;
      margin-left: 0;
    }
  }
  .ts-two-column {
    &.column:nth-child(2) {
      margin-left: 0;
    }
  }
  .ts-three-column,
  .ts-four-column {
    &.column {
      margin-left: 0;
    }
  }
}
</style>
